import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import cn from 'classnames';
import { Col, Form, Row } from 'antd';
import {
  AtsButtonMenu,
  AvatarLabel,
  Button,
  BUTTON_TYPES,
  Card,
  DatePicker,
  Input,
  notification,
  NOTIFICATION_TYPES,
  RichText,
  Status,
  TextArea,
} from '_atoms';
import { ReactComponent as Union } from 'dist/icons/Union.svg';
import { ReactComponent as Bin } from 'dist/icons/bin.svg';
import { BreadCrumbs } from '_atoms/BreadCrumbs';
import { Typography } from '_atoms/Typography';
import { PromotionRequestMainInfo } from '_molecules/PromotionRequestMainInfo';
import {
  PopupCancelPromotionRequest,
  PopupClosePromotionRequest,
  PopupDeclinePromotionRequest,
  PopupDiscardPromotionRequest,
} from '_molecules';
import { subject } from '@casl/ability';
import { useAppDispatch, useAppSelector } from 'store';
import { AccessDenied } from '_templates/AccessDenied/AccessDenied';
import { ACTIONS, ROLES, UI, useAbility } from 'permission';
import {
  approvePromotionRequest,
  cancelPromotionRequest,
  closePromotionRequest,
  declinePromotionRequest,
  getJobInfo,
  getOpeningById,
  getPromotionRequest,
  getTechnologyAreas,
  updatePromotionRequest,
} from 'actions';
import history from 'helpers/history';
import { dateFormatter } from 'helpers/dateFormatter';
import { getUser } from 'store/selectors/authorizeSelectors';
import {
  PROMOTION_PERIOD_TYPES,
  PROMOTION_REQUEST_APPROVE_DECLINE_STATUSES,
  PROMOTION_REQUEST_CANCEL_STATUSES,
  PROMOTION_REQUEST_CLOSE_STATUSES,
  PROMOTION_REQUEST_CONFIRM_PUBLICATION_STATUSES,
  PROMOTION_REQUEST_COPY_STATUSES,
  PROMOTION_REQUEST_FULL_EDIT_STATUSES,
  PROMOTION_REQUEST_LIMITED_EDIT_STATUSES,
  PROMOTION_REQUEST_STATUS_TYPES,
  PROMOTION_SOURCES,
} from 'constants/promotionRequest';
import {
  formValuesToMainInfoPayload,
  formValuesToPublicationPayload,
  officeOptions,
  promotionRequestToFormValues,
  teamLeadOptions,
  userOptions,
} from '_templates/CreatePromotionRequest/utils';
import type { PromotionRequest, PromotionRequestUser } from 'store/reducers/promotionRequestReducer';
import { DetailField, DetailSection } from './constants';
import { formatOfficeList, openingLabel, orderedOpenings } from './utils';
import './style.scss';

type LinkComponent = React.ComponentType<{ to: string; className?: string; children?: React.ReactNode }>;
const RouterLink = Link as LinkComponent;

type DetailsMode = 'view' | 'editMainInfo' | 'editPublication';

export const PromotionRequestDetails = () => {
  const { t } = useTranslation();
  const { id, requestId } = useParams<{ id: string; requestId: string }>();
  const { state: navState } = useLocation<{ request?: PromotionRequest }>();
  const routerHistory = useHistory();
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const ability = useAbility();
  const user = useAppSelector(getUser);

  const navRequest =
    routerHistory.action !== 'POP' && navState?.request && navState.request.uuid === requestId
      ? navState.request
      : null;
  const [request, setRequest] = useState<PromotionRequest | null>(navRequest);
  const [accessDenied, setAccessDenied] = useState(false);
  const [loading, setLoading] = useState(!navRequest);
  const [mode, setMode] = useState<DetailsMode>('view');
  const [saving, setSaving] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [openDecline, setOpenDecline] = useState(false);
  const [openCancel, setOpenCancel] = useState(false);
  const [openClose, setOpenClose] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [jobName, setJobName] = useState(navRequest?.job?.name ?? '');
  const publicationSectionRef = useRef<HTMLElement>(null);
  const mainInfoSectionRef = useRef<HTMLElement>(null);

  const [technologyAreas, setTechnologyAreas] = useState([]);
  const [searchRegionOptions, setSearchRegionOptions] = useState([]);
  const [recruiterLeadOptions, setRecruiterLeadOptions] = useState([]);
  const [teamLeadList, setTeamLeadList] = useState([]);

  const requiredRule = useMemo(() => [{ required: true, message: t('translation:validation.required') }], [t]);
  const periodTypeLabels = useMemo(
    () => ({
      [PROMOTION_PERIOD_TYPES.DAY]: t('translation:create_promotion_request.period_day'),
      [PROMOTION_PERIOD_TYPES.MONTH]: t('translation:create_promotion_request.period_month'),
    }),
    [t]
  );

  const fetchRequest = () =>
    getPromotionRequest(id, requestId, { showErrorNotification: false })
      .then(resp => {
        if (resp) setRequest(resp);
        return resp;
      })
      .catch((error: { response?: { status?: number } }) => {
        if (error?.response?.status === 403) setAccessDenied(true);
        return undefined;
      });

  useEffect(() => {
    if (!ability.can(ACTIONS.READ, UI.PROMOTION_REQUEST_VIEW)) {
      setAccessDenied(true);
      setLoading(false);
    }
  }, [ability]);

  useEffect(() => {
    if (navRequest) return;
    if (!ability.can(ACTIONS.READ, UI.PROMOTION_REQUEST_VIEW)) return;
    setLoading(true);
    fetchRequest().finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    dispatch(getJobInfo(id, false))
      .then(resp => {
        if (resp?.name) setJobName(resp.name);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (request?.job?.name) setJobName(request.job.name);
  }, [request?.job?.name]);

  useEffect(() => {
    if (mode === 'editMainInfo' && request) {
      form.setFieldsValue(promotionRequestToFormValues(request, periodTypeLabels));
      setHasUnsavedChanges(false);
    }
    if (mode === 'editPublication' && request) {
      form.setFieldsValue({
        publication_start_date: request.publication_start_date ? moment(request.publication_start_date) : null,
        publication_links: request.publication_links?.length ? request.publication_links : [''],
        publication_comment: request.publication_comment || '',
      });
      setHasUnsavedChanges(false);
    }
  }, [mode]);

  useEffect(() => {
    if (mode === 'view') return;

    const section = mode === 'editMainInfo' ? mainInfoSectionRef.current : publicationSectionRef.current;
    section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [mode]);

  if (accessDenied) return <AccessDenied />;

  if (loading || !request) return null;

  const { status } = request;
  const isEditMainInfo = mode === 'editMainInfo';
  const isEditPublication = mode === 'editPublication';
  const isEditing = mode !== 'view';

  const isRecruiter = user?.active_role?.name === ROLES.RECRUITER;
  const isHrpr = user?.active_role?.name === ROLES.HRPR;
  const isCreator = request.created_by?.uuid === user?.uuid;
  const isActor = !isRecruiter || isCreator;

  const canFullEdit =
    ability.can(ACTIONS.UPDATE, UI.PROMOTION_REQUEST_EDIT) &&
    isActor &&
    PROMOTION_REQUEST_FULL_EDIT_STATUSES.includes(status);
  const canUpdateMainInfo =
    ability.can(
      ACTIONS.UPDATE,
      subject(UI.PROMOTION_REQUEST_MAIN_INFO_EDIT, { promotion_source: request.promotion_source })
    ) &&
    isActor &&
    PROMOTION_REQUEST_LIMITED_EDIT_STATUSES.includes(status);
  const canCopy =
    ability.can(ACTIONS.READ, UI.PROMOTION_REQUEST_COPY) && PROMOTION_REQUEST_COPY_STATUSES.includes(status) && isActor;
  const canApproveDecline =
    ability.can(ACTIONS.READ, UI.PROMOTION_REQUEST_APPROVE_DECLINE) &&
    PROMOTION_REQUEST_APPROVE_DECLINE_STATUSES.includes(status);
  const canCancel =
    ability.can(ACTIONS.READ, UI.PROMOTION_REQUEST_CANCEL) &&
    PROMOTION_REQUEST_CANCEL_STATUSES.includes(status) &&
    isActor;
  const canClose =
    ability.can(ACTIONS.READ, subject(UI.PROMOTION_REQUEST_CLOSE, { promotion_source: request.promotion_source })) &&
    PROMOTION_REQUEST_CLOSE_STATUSES.includes(status) &&
    isActor;
  const canConfirmPublication =
    ability.can(
      ACTIONS.UPDATE,
      subject(UI.PROMOTION_REQUEST_PUBLICATION, { promotion_source: request.promotion_source })
    ) &&
    isActor &&
    PROMOTION_REQUEST_CONFIRM_PUBLICATION_STATUSES.includes(status);

  const isJobBoards = request.promotion_source === PROMOTION_SOURCES.JOB_BOARDS;
  const isPaidSocialMedia = request.promotion_source === PROMOTION_SOURCES.PPC;
  const sourceLabel = request.promotion_source;
  const hasPublicationDetails = request.has_publication_details && !!request.publication_start_date;

  const onFullEdit = () =>
    history.push({
      pathname: `/jobs/${id}/create-promotion-request/${requestId}`,
      state: { jobName: jobName || request.job?.name },
    });

  const loadEditOptions = async () => {
    setOptionsLoading(true);
    const [jobResp, taResp] = await Promise.all([
      dispatch(getJobInfo(id, false)).catch(() => null),
      getTechnologyAreas(id).catch(() => null),
    ]);
    if (jobResp) {
      if (jobResp.name) setJobName(jobResp.name);
      setRecruiterLeadOptions(userOptions(Array.isArray(jobResp.recruiter_leads) ? jobResp.recruiter_leads : []));
      setTeamLeadList(teamLeadOptions(jobResp));
    }
    setTechnologyAreas(
      (taResp || []).filter(area => !area.is_outdated).map(area => ({ value: area.uuid, label: area.name }))
    );
    const prior = request.prior_opening?.uuid;
    if (prior) {
      const opening = await getOpeningById(id, prior).catch(() => null);
      if (opening) setSearchRegionOptions(officeOptions(opening.offices || []));
    }
    setOptionsLoading(false);
  };

  const enterEditMainInfo = () => {
    setSearchRegionOptions(officeOptions(request.search_regions || []));
    setMode('editMainInfo');
    loadEditOptions();
  };

  const enterEditPublication = () => setMode('editPublication');

  const exitEdit = () => {
    setOpenConfirmation(false);
    setHasUnsavedChanges(false);
    form.resetFields();
    setMode('view');
  };

  const onValuesChange = () => setHasUnsavedChanges(true);

  const onCancel = () => {
    const { description, additional_requirements } = form.getFieldsValue();
    if (
      hasUnsavedChanges ||
      description !== form.getFieldValue('description_initial') ||
      additional_requirements !== form.getFieldValue('additional_requirements_initial')
    ) {
      setOpenConfirmation(true);
    } else {
      exitEdit();
    }
  };

  const onConfirm = values => {
    setSaving(true);
    updatePromotionRequest(id, requestId, formValuesToMainInfoPayload(values))
      .then(async resp => {
        if (resp) {
          await fetchRequest();
          notification({
            type: NOTIFICATION_TYPES.SUCCESS,
            message: t('translation:create_promotion_request.success_updated'),
          });
          exitEdit();
        } else {
          notification({
            type: NOTIFICATION_TYPES.ERROR,
            message: t('translation:create_promotion_request.error_updated'),
          });
        }
      })
      .finally(() => setSaving(false));
  };

  const onConfirmPublication = values => {
    setSaving(true);
    updatePromotionRequest(id, requestId, formValuesToPublicationPayload(values))
      .then(async resp => {
        if (resp) {
          await fetchRequest();
          notification({
            type: NOTIFICATION_TYPES.SUCCESS,
            message: t('translation:create_promotion_request.success_updated'),
          });
          exitEdit();
        } else {
          notification({
            type: NOTIFICATION_TYPES.ERROR,
            message: t('translation:create_promotion_request.error_updated'),
          });
        }
      })
      .finally(() => setSaving(false));
  };

  const onApprove = () => {
    setSaving(true);
    approvePromotionRequest(id, requestId)
      .then(async resp => {
        if (resp) {
          await fetchRequest();
          notification({
            type: NOTIFICATION_TYPES.SUCCESS,
            message: t('translation:promotion_request_details.success_approved'),
          });
        } else {
          notification({
            type: NOTIFICATION_TYPES.ERROR,
            message: t('translation:promotion_request_details.error_approved'),
          });
        }
      })
      .finally(() => setSaving(false));
  };

  const onDecline = ({ decline_reason }: { decline_reason: string }) => {
    setSaving(true);
    declinePromotionRequest(id, requestId, { decline_reason })
      .then(async resp => {
        if (resp) {
          await fetchRequest();
          setOpenDecline(false);
          notification({
            type: NOTIFICATION_TYPES.SUCCESS,
            message: t('translation:promotion_request_details.success_declined'),
          });
        } else {
          notification({
            type: NOTIFICATION_TYPES.ERROR,
            message: t('translation:promotion_request_details.error_declined'),
          });
        }
      })
      .finally(() => setSaving(false));
  };

  const onCancelRequest = ({ cancellation_comment }: { cancellation_comment: string }) => {
    setSaving(true);
    cancelPromotionRequest(id, requestId, { cancellation_comment })
      .then(async resp => {
        if (resp) {
          await fetchRequest();
          setOpenCancel(false);
          notification({
            type: NOTIFICATION_TYPES.SUCCESS,
            message: t('translation:promotion_request_details.success_canceled'),
          });
        } else {
          notification({
            type: NOTIFICATION_TYPES.ERROR,
            message: t('translation:promotion_request_details.error_canceled'),
          });
        }
      })
      .finally(() => setSaving(false));
  };

  const onCloseRequest = () => {
    setSaving(true);
    closePromotionRequest(id, requestId)
      .then(async resp => {
        if (resp) {
          await fetchRequest();
          setOpenClose(false);
          notification({
            type: NOTIFICATION_TYPES.SUCCESS,
            message: t('translation:promotion_request_details.success_closed'),
          });
        } else {
          notification({
            type: NOTIFICATION_TYPES.ERROR,
            message: t('translation:promotion_request_details.error_closed'),
          });
        }
      })
      .finally(() => setSaving(false));
  };

  const onCopy = () =>
    history.push({
      pathname: `/jobs/${id}/copy-promotion-request/${requestId}`,
      state: { jobName: jobName || request.job?.name },
    });

  const formatDate = (value?: string | null) => (value ? dateFormatter(value) : undefined);
  const person = (recipient?: PromotionRequestUser | null, role?: string) =>
    recipient ? (
      <AvatarLabel src={recipient.avatar} name={recipient.full_name} extraName={role ?? recipient.role} />
    ) : undefined;
  const people = (recipients: PromotionRequestUser[], role: string) =>
    recipients.length ? (
      <div className="promotion-request-details__people">
        {recipients.map(recipient => (
          <AvatarLabel key={recipient.uuid} src={recipient.avatar} name={recipient.full_name} extraName={role} />
        ))}
      </div>
    ) : undefined;
  const historyRow = (
    dateLabel: string,
    date: string | null | undefined,
    personLabel: string,
    who?: PromotionRequestUser | null,
    reasonLabel?: string,
    reason?: string | null
  ) =>
    date ? (
      <div className="promotion-request-details__field-row promotion-request-details__field-row--compact">
        <DetailField label={dateLabel}>{formatDate(date)}</DetailField>
        <DetailField label={personLabel}>{person(who)}</DetailField>
        {reason && reasonLabel && <DetailField label={reasonLabel}>{reason}</DetailField>}
      </div>
    ) : null;

  const renderPublicationDetails = () => (
    <DetailSection title={t('translation:promotion_request_details.section_publication_details')}>
      {hasPublicationDetails ? (
        <>
          <div className="promotion-request-details__field-row promotion-request-details__field-row--equal">
            <DetailField label={t('translation:promotion_request_details.label_start_date')}>
              {formatDate(request.publication_start_date)}
            </DetailField>
            <DetailField label={t('translation:promotion_request_details.label_end_date')}>
              {formatDate(request.publication_end_date)}
            </DetailField>
          </div>
          <DetailField label={t('translation:promotion_request_details.label_links')}>
            {request.publication_links?.length
              ? request.publication_links.map((link, index) => (
                  <a
                    key={`${link}-${index}`}
                    className="promotion-request-details__link"
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {link}
                  </a>
                ))
              : undefined}
          </DetailField>
          <DetailField label={t('translation:promotion_request_details.label_comment')}>
            {request.publication_comment ? <RichText text={request.publication_comment} /> : undefined}
          </DetailField>
        </>
      ) : (
        <div className="promotion-request-details__empty-value">—</div>
      )}
    </DetailSection>
  );

  const renderPublicationDetailsEdit = () => (
    <DetailSection
      title={t('translation:promotion_request_details.section_publication_details')}
      sectionRef={publicationSectionRef}
    >
      <Row gutter={[32, 16]}>
        <Col lg={12} md={12} sm={24}>
          <DatePicker
            label={t('translation:promotion_request_details.label_start_date')}
            itemProps={{ name: 'publication_start_date', rules: requiredRule }}
          />
        </Col>
      </Row>
      <Form.List name="publication_links">
        {(fields, { add, remove }) => (
          <>
            {fields.map((field, index) => (
              <div className={cn('promotion-request-details__link-row', { first: index === 0 })} key={field.key}>
                <Input
                  label={index === 0 ? t('translation:promotion_request_details.label_links') : undefined}
                  optional={index === 0}
                  placeholder={t('translation:promotion_request_details.ph_links')}
                  maxLength={1000}
                  itemProps={{
                    name: field.name,
                    rules: [{ type: 'url', message: t('translation:validation.invalid_url') }],
                  }}
                />
                {fields.length > 1 && (
                  <Button
                    className="promotion-request-details__link-remove"
                    type={BUTTON_TYPES.GHOST}
                    variant="grey"
                    icon={<Bin />}
                    onClick={() => remove(field.name)}
                  />
                )}
              </div>
            ))}
            <Button className="promotion-request-details__add-link" icon={<Union />} onClick={() => add('')}>
              {t('translation:promotion_request_details.button_add_link')}
            </Button>
          </>
        )}
      </Form.List>
      <TextArea
        label={t('translation:promotion_request_details.label_comment')}
        placeholder={t('translation:promotion_request_details.ph_comment')}
        maxLength={6000}
        itemProps={{ name: 'publication_comment', rules: requiredRule }}
      />
    </DetailSection>
  );

  const renderPromotionInfo = () => (
    <DetailSection title={t('translation:promotion_request_details.section_promotion_info')}>
      <div className="promotion-request-details__field-row promotion-request-details__field-row--equal">
        <DetailField label={t('translation:promotion_request_details.label_promotion_source')}>
          {sourceLabel}
        </DetailField>
        <DetailField label={t('translation:promotion_request_details.label_promotion_period')}>
          {request.promotion_period_display}
        </DetailField>
      </div>
      {isJobBoards && (
        <DetailField label={t('translation:promotion_request_details.label_job_boards')}>
          {request.job_boards.join(', ')}
        </DetailField>
      )}
      {isPaidSocialMedia && (
        <DetailField label={t('translation:promotion_request_details.label_platform')}>
          {request.platform.join(', ')}
        </DetailField>
      )}
    </DetailSection>
  );

  const renderMainInfoReadOnly = () => (
    <DetailSection title={t('translation:promotion_request_details.section_main_info')}>
      <DetailField label={t('translation:promotion_request_details.label_openings')}>
        <div className="promotion-request-details__openings">
          {orderedOpenings(request).map(opening => (
            <div key={opening.uuid}>
              <RouterLink
                className="promotion-request-details__opening-title"
                to={`/jobs/${id}/show/openings?scroll=${opening.uuid}`}
              >
                {openingLabel(opening)}
              </RouterLink>
              {opening.expected_date && (
                <div className="promotion-request-details__opening-meta">
                  {t('translation:promotion_request_details.label_expected_on')} {dateFormatter(opening.expected_date)}
                </div>
              )}
              {!!opening.offices?.length && (
                <div className="promotion-request-details__opening-meta">{formatOfficeList(opening.offices)}</div>
              )}
            </div>
          ))}
        </div>
      </DetailField>

      <DetailField label={t('translation:promotion_request_details.label_description')}>
        <RichText text={request.description} />
      </DetailField>

      <DetailField label={t('translation:promotion_request_details.label_search_region')}>
        {request.search_regions.map(office => office.name).join(', ')}
      </DetailField>

      <DetailField label={t('translation:promotion_request_details.label_technology_area')}>
        {request.technology_areas.map(area => area.name).join(', ')}
      </DetailField>

      <DetailField label={t('translation:promotion_request_details.label_translation_languages')}>
        {request.translation_languages.join(', ')}
      </DetailField>

      <DetailField label={t('translation:promotion_request_details.label_additional_requirements')}>
        {request.additional_requirements ? <RichText text={request.additional_requirements} /> : undefined}
      </DetailField>

      <DetailField label={t('translation:promotion_request_details.label_recruiter_lead')}>
        {people(request.recruiter_leads, t('translation:promotion_request_details.role_recruiter_lead'))}
      </DetailField>

      <DetailField label={t('translation:promotion_request_details.label_notification_recipients')}>
        {people(request.team_lead_notification_recipients, t('translation:promotion_request_details.role_team_lead'))}
      </DetailField>
    </DetailSection>
  );

  const renderMainInfoEdit = () => {
    const openingsSummary = orderedOpenings(request)
      .map(
        opening =>
          `${openingLabel(opening)}${
            opening.uuid === request.prior_opening?.uuid
              ? ` (${t('translation:create_promotion_request.label_prior')})`
              : ''
          }`
      )
      .join(', ');
    return (
      <DetailSection
        title={t('translation:promotion_request_details.section_main_info')}
        sectionRef={mainInfoSectionRef}
      >
        <Row gutter={[32, 16]}>
          <Col lg={12} md={12} sm={24}>
            <Input
              disabled
              value={jobName || request.job?.name}
              label={t('translation:create_promotion_request.label_job')}
            />
          </Col>
          <Col lg={12} md={12} sm={24}>
            <Input disabled value={openingsSummary} label={t('translation:create_promotion_request.label_opening')} />
          </Col>
        </Row>
        <PromotionRequestMainInfo
          requiredRule={requiredRule}
          technologyAreas={technologyAreas}
          searchRegionOptions={searchRegionOptions}
          recruiterLeadOptions={recruiterLeadOptions}
          teamLeadList={teamLeadList}
          lockUntilOpening={false}
          loading={optionsLoading}
        />
      </DetailSection>
    );
  };

  const renderChangeHistory = () => (
    <DetailSection title={t('translation:promotion_request_details.section_change_history')}>
      {historyRow(
        t('translation:promotion_request_details.label_creation_date'),
        request.created_at,
        t('translation:promotion_request_details.label_created_by'),
        request.created_by
      )}
      {historyRow(
        t('translation:promotion_request_details.label_approval_date'),
        request.approval_date,
        t('translation:promotion_request_details.label_approved_by'),
        request.approved_by
      )}
      {historyRow(
        t('translation:promotion_request_details.label_decline_date'),
        request.decline_date,
        t('translation:promotion_request_details.label_declined_by'),
        request.declined_by,
        t('translation:promotion_request_details.label_decline_reason'),
        request.decline_reason
      )}
      {historyRow(
        t('translation:promotion_request_details.label_publication_date'),
        request.publication_date,
        t('translation:promotion_request_details.label_published_by'),
        request.published_by
      )}
      {historyRow(
        t('translation:promotion_request_details.label_update_date'),
        request.last_updated_at,
        t('translation:promotion_request_details.label_updated_by'),
        request.last_updated_by
      )}
      {historyRow(
        t('translation:promotion_request_details.label_cancellation_date'),
        request.cancellation_date,
        t('translation:promotion_request_details.label_canceled_by'),
        request.canceled_by,
        t('translation:promotion_request_details.label_cancellation_reason'),
        request.cancellation_comment
      )}
      {request.completion_date && (
        <div className="promotion-request-details__field-row promotion-request-details__field-row--compact">
          <DetailField label={t('translation:promotion_request_details.label_completion_date')}>
            {formatDate(request.completion_date)}
          </DetailField>
        </div>
      )}
      {historyRow(
        t('translation:promotion_request_details.label_closure_date'),
        request.closure_date,
        t('translation:promotion_request_details.label_closed_by'),
        request.closed_by
      )}
    </DetailSection>
  );

  const editTitlePrefix = isEditPublication
    ? t('translation:promotion_request_details.button_confirm_publication')
    : t('translation:create_promotion_request.title_edit_main_info');
  const editTitle = `${editTitlePrefix} — ${request.request_title}`;

  const content = (
    <>
      <div className="promotion-request-details__header-wrapper">
        <Card className="promotion-request-details__header">
          <div className="promotion-request-details__header-title">
            <Typography.Title1>{isEditing ? editTitle : request.request_title}</Typography.Title1>
            {!isEditing && <Status type={PROMOTION_REQUEST_STATUS_TYPES[status]}>{status}</Status>}
          </div>
          <div className="promotion-request-details__header-actions">
            {isEditing ? (
              <>
                <Button disabled={saving} onClick={onCancel}>
                  {t('translation:create_promotion_request.button_cancel')}
                </Button>
                <Button htmlType="submit" loading={saving} type={BUTTON_TYPES.PRIMARY}>
                  {t('translation:create_promotion_request.button_confirm')}
                </Button>
              </>
            ) : (
              <>
                {canApproveDecline && (
                  <Button type={BUTTON_TYPES.PRIMARY} loading={saving} onClick={onApprove}>
                    {t('translation:promotion_request_details.button_approve')}
                  </Button>
                )}
                {canConfirmPublication && (
                  <Button type={BUTTON_TYPES.PRIMARY} onClick={enterEditPublication}>
                    {t('translation:promotion_request_details.button_confirm_publication')}
                  </Button>
                )}
                {canClose && (
                  <Button type={BUTTON_TYPES.PRIMARY} onClick={() => setOpenClose(true)}>
                    {t('translation:promotion_request_details.button_close_request')}
                  </Button>
                )}
                <AtsButtonMenu>
                  {canFullEdit && (
                    <li role="none" onClick={onFullEdit}>
                      {t('translation:promotion_request_details.menu_edit_request')}
                    </li>
                  )}
                  {canUpdateMainInfo && (
                    <li role="none" onClick={enterEditMainInfo}>
                      {t('translation:promotion_request_details.menu_edit_main_info')}
                    </li>
                  )}
                  {canCopy && (
                    <li role="none" onClick={onCopy}>
                      {t('translation:promotion_request_details.menu_copy_request')}
                    </li>
                  )}
                  {canCancel && (
                    <li role="none" className="red" onClick={() => setOpenCancel(true)}>
                      {t('translation:promotion_request_details.menu_cancel_request')}
                    </li>
                  )}
                  {canApproveDecline && (
                    <li role="none" className="red" onClick={() => setOpenDecline(true)}>
                      {t('translation:promotion_request_details.menu_decline')}
                    </li>
                  )}
                </AtsButtonMenu>
              </>
            )}
          </div>
        </Card>
      </div>

      <Card className="promotion-request-details__body">
        {isEditPublication ? renderPublicationDetailsEdit() : renderPublicationDetails()}
        {renderPromotionInfo()}
        {isEditMainInfo ? renderMainInfoEdit() : renderMainInfoReadOnly()}
        {renderChangeHistory()}
      </Card>
    </>
  );

  return (
    <div className="wrapper-content promotion-request-details">
      <BreadCrumbs
        path={isHrpr ? '/dashboard' : '/jobs'}
        element={t(
          isHrpr
            ? 'translation:promotion_request_details.breadcrumb_dashboard'
            : 'translation:promotion_request_details.breadcrumb_jobs'
        )}
        crumbs={
          isHrpr ? [] : [{ path: `/jobs/${id}/show/promotion-requests`, label: jobName || request.job?.name || '' }]
        }
        elementName={isEditing ? editTitle : request.request_title}
      />

      {isEditing ? (
        <Form
          form={form}
          onFinish={isEditPublication ? onConfirmPublication : onConfirm}
          onValuesChange={onValuesChange}
          validateTrigger="onSubmit"
        >
          {content}
        </Form>
      ) : (
        content
      )}

      <PopupDiscardPromotionRequest
        open={openConfirmation}
        onClose={() => setOpenConfirmation(false)}
        onConfirm={exitEdit}
      />

      <PopupDeclinePromotionRequest
        open={openDecline}
        requestTitle={request.request_title}
        loading={saving}
        onClose={() => setOpenDecline(false)}
        onSubmit={onDecline}
      />

      <PopupCancelPromotionRequest
        open={openCancel}
        requestTitle={request.request_title}
        loading={saving}
        onClose={() => setOpenCancel(false)}
        onSubmit={onCancelRequest}
      />

      <PopupClosePromotionRequest
        open={openClose}
        requestTitle={request.request_title}
        loading={saving}
        onClose={() => setOpenClose(false)}
        onSubmit={onCloseRequest}
      />
    </div>
  );
};
